/**
 * Sprint 19: Saved Views Dropdown
 * 
 * Allows pin/unpin of hierarchy views (Epics, Features, Stories, Tasks)
 */

import React, { useState, useEffect } from 'react';
import { 
  Bookmark, BookmarkPlus, Pin, PinOff, ChevronDown, 
  Layers, FileText, CheckSquare, ExternalLink
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

const ICON_MAP = {
  Layers: Layers,
  FileText: FileText,
  CheckSquare: CheckSquare
};

const SavedViewsDropdown = ({ currentViewType, onNavigate, className = '' }) => {
  const [views, setViews] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadViews();
  }, []);

  const loadViews = async () => {
    try {
      const response = await api.getSavedViews();
      setViews(response.data?.views || []);
    } catch (error) {
      console.error('Failed to load saved views:', error);
    }
  };

  const togglePin = async (view) => {
    setLoading(true);
    try {
      await api.updateSavedView(view.id, { is_pinned: !view.is_pinned });
      toast.success(view.is_pinned ? 'View unpinned' : 'View pinned');
      loadViews();
      // Dispatch event to update sidebar
      window.dispatchEvent(new CustomEvent('savedViewsUpdated'));
    } catch (error) {
      toast.error('Failed to update view');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Layers;
    return <IconComponent className="w-4 h-4" />;
  };

  const pinnedViews = views.filter(v => v.is_pinned);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
        data-testid="saved-views-dropdown"
      >
        <Bookmark className="w-4 h-4 text-[var(--clr-brand-primary)]" />
        <span>Saved Views</span>
        {pinnedViews.length > 0 && (
          <span className="px-1.5 py-0.5 bg-[var(--clr-brand-primary)] text-white text-xs rounded-full">
            {pinnedViews.length}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setOpen(false)} 
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 w-64 bg-[var(--clr-surface-card)] rounded-lg shadow-xl border border-[var(--clr-border-light)] z-50">
            <div className="p-2 border-b border-[var(--clr-border-light)]">
              <p className="text-xs text-[var(--clr-text-muted)] font-medium px-2">
                HIERARCHY VIEWS
              </p>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {views.map((view) => (
                <div
                  key={view.id}
                  className={`flex items-center justify-between px-3 py-2 hover:bg-[var(--clr-surface-hover)] ${
                    view.view_type === currentViewType ? 'bg-[var(--clr-brand-primary)] bg-opacity-10' : ''
                  }`}
                >
                  <button
                    onClick={() => {
                      onNavigate?.(view.path);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    {getIcon(view.icon)}
                    <span className={`text-sm ${
                      view.view_type === currentViewType 
                        ? 'font-medium text-[var(--clr-brand-primary)]' 
                        : 'text-[var(--clr-text-primary)]'
                    }`}>
                      {view.name}
                    </span>
                    {view.is_default && (
                      <span className="text-xs text-[var(--clr-text-muted)]">(default)</span>
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(view);
                    }}
                    disabled={loading}
                    className={`p-1.5 rounded hover:bg-[var(--clr-surface-subtle)] ${
                      view.is_pinned ? 'text-[var(--clr-brand-primary)]' : 'text-[var(--clr-text-muted)]'
                    }`}
                    title={view.is_pinned ? 'Unpin' : 'Pin to sidebar'}
                    data-testid={`pin-view-${view.view_type}`}
                  >
                    {view.is_pinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SavedViewsDropdown;
